import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { mutationFor, queryFor, useApiConfig } from "@visioni/a3s-client-api/app";
import {
  controlPatriotBackfillEndpoint,
  listPatriotBackfillsEndpoint,
  startPatriotBackfillEndpoint,
  type ControlPatriotBackfillRequest,
  type ControlPatriotBackfillResponse,
  type PatriotBackfillRun,
  type StartPatriotBackfillRequest,
  type StartPatriotBackfillResponse,
} from "../../client-messages/patriot-backfill";

import { useCursorPager, type CursorPager } from "./cursor-pager";

const startPatriotBackfillMutation = mutationFor(startPatriotBackfillEndpoint);
const controlPatriotBackfillMutation = mutationFor(controlPatriotBackfillEndpoint);
const listPatriotBackfillsQuery = queryFor(listPatriotBackfillsEndpoint);

/** Runs per page of the backfill history. The endpoint caps at 100. */
export const BACKFILL_RUNS_PAGE_SIZE = 10;

/** How often the history refreshes while it waits for a run just started to appear. */
const BACKFILL_RUNS_POLL_MS = 10_000;

/**
 * Starts a Patriot backfill and resolves once the reconciler has the run, not
 * when it finishes. Support-only and behind `patriot_tools_backfill`: the API
 * answers 403 to anyone else, and 501 where there is no PDS to backfill.
 */
export function useStartPatriotBackfill() {
  const mutationConfig = useMutationConfig();

  return useMutation<StartPatriotBackfillResponse, Error, StartPatriotBackfillRequest>({
    mutationFn: (request) => startPatriotBackfillMutation(mutationConfig, {}).mutationFn(request),
  });
}

/**
 * Pauses, resumes or stops a backfill, resolving once its passes have changed
 * state. Same gate as starting one; 404 for a run that isn't a backfill.
 */
export function useControlPatriotBackfill() {
  const mutationConfig = useMutationConfig();

  return useMutation<ControlPatriotBackfillResponse, Error, { readonly runId: string } & ControlPatriotBackfillRequest>(
    {
      mutationFn: ({ runId, action }) =>
        controlPatriotBackfillMutation(mutationConfig, { runId }).mutationFn({ action }),
    },
  );
}

function useMutationConfig() {
  const apiConfig = useApiConfig();
  return apiConfig.tenant ?
      {
        tenantId: apiConfig.tenant.tenantId,
        apiBaseUrl: apiConfig.tenant.apiBaseUrl,
        authenticatedFetch: apiConfig.authenticatedFetch,
      }
    : { authenticatedFetch: apiConfig.authenticatedFetch };
}

export interface UseBackfillRunsOptions {
  /**
   * Keep refreshing until this time (epoch ms), so a run just started appears
   * once the reconciler records it, a few seconds after the API answers.
   */
  readonly pollUntil?: number | undefined;
}

export interface UseBackfillRunsResult {
  readonly items: readonly PatriotBackfillRun[] | undefined;
  readonly isLoading: boolean;
  readonly isFetching: boolean;
  readonly error: Error | null;
  readonly refetch: () => void;
  /** When the page shown was fetched (epoch ms), so a stale one can be told apart; absent before the first fetch. */
  readonly fetchedAt: number | undefined;
  readonly pager: CursorPager;
  /** The cursor for the next page, or `null` when there are no older runs. */
  readonly nextCursor: string | null;
}

/**
 * Every backfill run, fleet-wide and newest first, paged like the activation
 * lists. Until `pollUntil`, the page refreshes itself.
 */
export function useBackfillRuns({ pollUntil }: UseBackfillRunsOptions = {}): UseBackfillRunsResult {
  const apiConfig = useApiConfig();
  const pager = useCursorPager("backfill-runs");

  const query = useQuery({
    ...listPatriotBackfillsQuery(
      apiConfig,
      {},
      { limit: BACKFILL_RUNS_PAGE_SIZE, ...(pager.cursor !== undefined && { cursor: pager.cursor }) },
    ),
    placeholderData: keepPreviousData,
    // The app persists its query cache and treats it as fresh for five
    // minutes, so a reload or a return to the page would show a run's progress
    // as it was when last fetched. Progress moves by the minute: re-read it on
    // every mount.
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: () => (pollUntil !== undefined && Date.now() < pollUntil ? BACKFILL_RUNS_POLL_MS : false),
  });

  return {
    items: query.data?.items,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => void query.refetch(),
    fetchedAt: query.dataUpdatedAt > 0 ? query.dataUpdatedAt : undefined,
    pager,
    nextCursor: query.isPlaceholderData ? null : (query.data?.cursor ?? null),
  };
}
